<template>
  <v-col>
    <v-window v-model="step">
      <v-window-item :value="1">
        <div class="text-h4 mb-4">
          Certificate of Exemption from Federal Excise Tax on Communication Services
        </div>
        <v-row>
          <v-col cols="6">
            <div class="subtitle-2">What is the company name?</div>
            <v-text-field
              v-model="formData.fedtax_company_name"
              label="Company Name"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <AddressAutocomplete
              :value="fedTaxAddress"
              @registerAddressChange="setAddress($event)"
            />
          </v-col>
        </v-row>
      </v-window-item>

      <v-window-item :value="2">
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <div class="subtitle-2">Exemption requested for the following accounts:</div>
            <v-text-field
              v-model="formData.fedtax_accounts_exempt"
              label="Exempt Account"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <div class="subtitle-2">Please check one of the following:</div>
            <v-radio-group v-model="formData.fedtax_exemption_type">
              <template v-slot:label>
                <div>
                  Let us know if this is a new certificate or a renewal.
                </div>
              </template>
              <v-radio value="new">
                <template v-slot:label>
                  <div>New Exemption Certificate</div>
                </template>
              </v-radio>
              <v-radio value="renewal">
                <template v-slot:label>
                  <div>Renewal Exemption Certificate</div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
      </v-window-item>

      <v-window-item :value="3">
        <v-row>
          <v-col cols="12">
            <div class="subtitle-2 pb-3">
              THE UNDERSIGNED REPRESENTS THAT HE/SHE IS AUTHORIZED TO EXECUTE THIS
              CERTIFICATION<br>
              AND HEREBY CLAIMS EXEMPTION FROM TAXES IMPOSED BY SECTION 4251 OF THE<br>
              INTERNAL REVENUE CODE UNDER THE FOLLOWING PROVISIONS.
            </div>
            <div class="subtitle-1 pb-3">
              (Select the appropriate section and provide required information where applicable):
            </div>

            <v-row>
              <v-col cols="6">
                <v-autocomplete
                  v-model="formData.fedtax_exemption_provision"
                  :items="getProvisions"
                  dense
                  label="Exemption Provision"
                  item-value="value"
                  item-text="label"
                  @input="() => collectProvisionInfo = true"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <ProvisionForm
          :provision="formData.fedtax_exemption_provision"
          :show="collectProvisionInfo"
          @setProvision="setProvisionInfo"
          @closeModal="(e) => closeModal(e)"
        />
      </v-window-item>

      <v-window-item :value="4">
        <ESignature
          v-model="formData.fedtax_sig_name"
          :accepted.sync="formData.fedtax_accepted"
          :preamble="signaturePreamble"
        >
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.fedtax_billing_name"
                label="Account Billing Name"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.fedtax_contact_phone"
                label="Contact Phone Number"
                mask="(xxx)xxx-xxxx"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="formData.fedtax_title"
                label="Title"
              ></v-text-field>
            </v-col>
          </v-row>
        </ESignature>
      </v-window-item>
    </v-window>
  </v-col>
</template>

<script>
import AddressAutocomplete from "@/components/formfields/AddressAutocomplete.vue";
import ESignature from "@/components/formfields/ESignature.vue";
import ProvisionForm from '@/components/dialogs/ProvisionForm.vue';
import { mapActions, mapGetters } from "vuex";

export default {
  name: "FederalExcise",
  components: {
    AddressAutocomplete,
    ESignature,
    ProvisionForm,
  },
  data() {
    return {
      step: 1,
      collectProvisionInfo: false,
      upload: false,
      formData: {},
      signaturePreamble: `
        <div class="text-h4">Authorized Representative Signature</div>
        <div class="subtitle-2 mb-4">
          The undersigned agrees to notify the Telephone Company in writing within
          30 days of when the basis for tax exemption indicated above changes or ceases to exist.
          The undersigned understands that the fraudulent use of this certificate for the
          purpose of securing this exemption will subject him/her and all guilty parties to a fine
          of not more than $10,000 or to imprisonment for not more than five years,or both
          together with costs of prosecution
        </div>
      `,
    };
  },
  mounted() {
    const vm = this;
    const generalData = this.getFormData.general;
    this.$set(this.formData, 'fedtax_company_name', generalData.fedtax_company_name);
    this.$set(this.formData, 'fedtax_accounts_exempt', generalData.fedtax_accounts_exempt);
    this.$set(this.formData, 'fedtax_exemption_type', generalData.fedtax_exemption_type);
    this.$set(this.formData, 'fedtax_exemption_provision', generalData.fedtax_exemption_provision);
    this.$set(this.formData, 'fedtax_sig_name', generalData.fedtax_sig_name);
    this.$set(this.formData, 'fedtax_accepted', generalData.fedtax_accepted);
    this.$set(this.formData, 'fedtax_billing_name', generalData.fedtax_billing_name);
    this.$set(this.formData, 'fedtax_contact_phone', generalData.fedtax_contact_phone);
    this.$set(this.formData, 'fedtax_title', generalData.fedtax_title);

    this.$set(this.formData, 'fedtax_address', generalData.fedtax_address);
    this.$set(this.formData, 'fedtax_suite', generalData.fedtax_suite);
    this.$set(this.formData, 'fedtax_city', generalData.fedtax_city);
    this.$set(this.formData, 'fedtax_state', generalData.fedtax_state);
    this.$set(this.formData, 'fedtax_zip', generalData.fedtax_zip);

    if (!this.review) {
      this.hijackStepper((goRight) => {
        if ((vm.step === 1 && !goRight) || (vm.step === 4 && goRight)) {
          vm.unHijackStepper();
          vm.nextSubTab(goRight);
        }
        vm.step += goRight ? 1 : -1;
      });
    }
  },
  beforeDestroy() {
    this.unHijackStepper();
  },
  methods: {
    ...mapActions([
      "updateStoreFormData",
      "hijackStepper",
      "unHijackStepper",
      "nextSubTab",
    ]),
    setAddress(newAddress) {
      this.$set(this.formData, 'fedtax_address', newAddress.streetAddress);
      this.$set(this.formData, 'fedtax_city', newAddress.suite);
      this.$set(this.formData, 'fedtax_city', newAddress.city);
      this.$set(this.formData, 'fedtax_state', newAddress.state);
      this.$set(this.formData, 'fedtax_zip', newAddress.zip);
    },
    setProvisionInfo(e) {
      const provision = this.formData.fedtax_exemption_provision;

      // set all other provisions to null if they exist ....
      const keys = Object.keys(e[provision]);

      keys.forEach((key) => {
        this.$set(this.formData, key, e[provision][key].value);
      });

      this.closeModal({ cancel: false });
    },
    registerFormChange(val) {
      const keys = Object.keys(val);
      keys.forEach((key) => {
        this.updateStoreFormData({
          data: val[key],
          main: 'general',
          sub: key,
        });
      });
    },
    closeModal({ cancel }) {
      if (cancel) {
        this.formData.fedtax_exemption_provision = '';
      }
      this.collectProvisionInfo = false;
    },
  },
  computed: {
    ...mapGetters(['getFormData', 'getProvisions']),
    fedTaxAddress() {
      const address = {};
      if (this.formData.fedtax_address) {
        // Use saved data if it exists
        address.streetAddress = this.formData.fedtax_address;
        address.suite = this.formData.fedtax_suite;
        address.city = this.formData.fedtax_city;
        address.state = this.formData.fedtax_state;
        address.zip = this.formData.fedtax_zip;
      }
      return address;
    },
  },
  watch: {
    formData: {
      handler(val) {
        this.registerFormChange(val);
      },
      deep: true,
    },
  },
};
</script>
